<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline">
        {{ $t('scheduleDefinitionRateChange') }}
      </span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>

    <v-card-text class="mt-4">
      <v-window v-model="step">
        <v-window-item :value="1">
          <ReadOnlyAttributeCard
          :label="$t('effectiveDate')"
          :value="displayEffectiveDate">
            <template #append>
              <div style="pointer-events: auto;">
                <Icon
                icon="mdi-pencil"
                iconColor="success"
                :small="false"
                :tooltipText="$t('editEffectiveDate')"
                margin=""
                @icon-clicked="openEditEffectiveDateTimeDialog"/>
              </div>
            </template>
          </ReadOnlyAttributeCard>

          <v-text-field
          v-model="note"
          :label="$t('noteOptional')"
          maxLength="128"
          counter="128"
          clearable
          outlined
          dense
          class="mt-4"/>

          <span class="subtitle-1 font-weight-medium mt-4 d-block mb-2">{{ $t('selectDefinitionsForRateChange') }}</span>
          <v-card outlined style="max-height: 300px; overflow-y: auto;">
            <v-list dense class="py-0">
              <template v-for="(definition, index) in definitions">
                <v-divider v-if="index > 0" :key="'d-' + definition.rateSheetDefinitionId"/>
                <v-list-item
                :key="definition.rateSheetDefinitionId"
                @click="toggleDefinition(definition.rateSheetDefinitionId)">
                  <v-list-item-action class="mr-3">
                    <v-checkbox
                    :input-value="selectedDefinitionIds.includes(definition.rateSheetDefinitionId)"
                    color="primary"
                    hide-details
                    class="mt-0 pt-0"
                    @click.stop="toggleDefinition(definition.rateSheetDefinitionId)"/>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="subtitle-1 font-weight-regular">{{ definition.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-window-item>

        <v-window-item :value="2">
          <v-row v-if="statsLoading" justify="center" class="py-8">
            <v-progress-circular indeterminate color="primary"/>
          </v-row>

          <template v-else>
            <NoData v-if="!impactedContracts || impactedContracts.length === 0">
              <span>{{ $t('noImpactedContracts') }}</span>
            </NoData>

            <template v-else>
              <v-row align="center" no-gutters class="mb-1">
                <v-col>
                  <span class="subtitle-1 font-weight-medium">{{ $t('selectContractsForRateChange') }}</span>
                  <span class="subtitle-2 grey--text ml-3" v-if="totalSelectedActivities > 0">
                    {{ $tc('activityCount', totalSelectedActivities) }} across {{ $tc('contractCount', selectedContractCount) }} selected
                  </span>
                </v-col>
              </v-row>

              <v-row align="center" no-gutters class="mb-2">
                <v-col cols="4">
                  <v-text-field
                  v-model="contractSearch"
                  :placeholder="$t('search')"
                  prepend-inner-icon="mdi-magnify"
                  dense outlined hide-details clearable/>
                </v-col>
                <v-spacer/>
                <v-col cols="auto" class="d-flex align-center">
                  <ReadOnlyAttributeCard
                  style="min-width: 240px;"
                  :label="$t('effectiveDate')"
                  :value="displayEffectiveDate">
                    <template #append>
                      <div style="pointer-events: auto;">
                        <Icon
                        icon="mdi-pencil"
                        iconColor="success"
                        :small="false"
                        :tooltipText="$t('editEffectiveDate')"
                        margin=""
                        @icon-clicked="openEditEffectiveDateTimeDialog"/>
                      </div>
                    </template>
                  </ReadOnlyAttributeCard>
                </v-col>
              </v-row>

              <v-data-table
              :headers="contractHeaders"
              :items="impactedContracts"
              :search="contractSearch"
              item-key="contractId"
              dense
              show-expand
              :item-class="contractRowClass"
              :items-per-page="15"
              :hide-default-footer="impactedContracts.length <= 15"
              class="elevation-0 schedule-contracts-table">

                <template #header.select>
                  <v-simple-checkbox
                  color="success"
                  :value="allActivitiesSelected"
                  :indeterminate="someActivitiesSelected"
                  @input="toggleAllActivities"
                  :ripple="false"/>
                </template>

                <template #header.accountName="{ header }">
                  <Icon :icon="IconHelpers.getIconForObjectType('account')" :small="false" iconColor="grey"/><span>{{ header.text }}</span>
                </template>
                <template #header.destinationAccountName="{ header }">
                  <Icon :icon="IconHelpers.getIconForObjectType('destination')" :small="false" iconColor="grey"/><span>{{ header.text }}</span>
                </template>
                <template #header.tractName="{ header }">
                  <Icon :icon="IconHelpers.getIconForObjectType('tract')" :small="false" iconColor="grey"/><span>{{ header.text }}</span>
                </template>
                <template #header.setting="{ header }">
                  <Icon :icon="IconHelpers.getIconForObjectType('setting')" :small="false" iconColor="grey"/><span>{{ header.text }}</span>
                </template>

                <template #item.select="{ item }">
                  <v-simple-checkbox
                  color="success"
                  :value="allActivitiesSelectedForContract(item)"
                  :indeterminate="someActivitiesSelectedForContract(item)"
                  :disabled="isContractDisabled(item)"
                  @input="toggleContractActivities(item, $event)"
                  :ripple="false"/>
                </template>

                <template #item.status="{ item }">
                  <Icon
                  v-if="item.hasActiveApproval"
                  icon="mdi-file-document-check-outline"
                  iconColor="error"
                  :small="false"
                  :tooltipText="$t('pendingApproval')"
                  margin="mr-1"/>
                  <Icon
                  v-if="item.hasPendingScheduledRateChange"
                  icon="invoice-text-update"
                  iconColor="error"
                  :small="false"
                  :tooltipText="$t('pendingRateChange')"
                  margin=""/>
                </template>

                <template #item.actions="{ item }">
                  <Icon
                  icon="mdi-information-outline"
                  :small="false"
                  margin=""
                  :tooltipText="$t('details')"
                  @icon-clicked="showContractDetail(item)"/>
                </template>

                <template #expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="expanded-activity-cell">
                    <div class="expanded-activity-cards">
                      <v-card
                      v-for="activity in item.activities"
                      :key="activity.activityId"
                      outlined
                      class="expanded-activity-card"
                      :class="{
                        'disabled-activity': isContractDisabled(item) || activity.hasLockedLine,
                        'excluded-activity': !isContractDisabled(item) && !activity.hasLockedLine && !isActivitySelected(item.contractId, activity.activityId)
                      }">
                        <div class="d-flex align-start px-3 py-2">
                          <div class="mt-1">
                            <Icon
                            v-if="activity.hasLockedLine"
                            icon="mdi-lock"
                            iconColor="grey"
                            :small="false"
                            margin="mr-3"
                            :tooltipText="$t('activityHasLockedLine')"/>
                            <Icon
                            v-else-if="!isContractDisabled(item) && isActivitySelected(item.contractId, activity.activityId)"
                            icon="mdi-close-circle-outline"
                            iconColor="grey"
                            :small="false"
                            margin="mr-3"
                            :tooltipText="$t('exclude')"
                            @icon-clicked="toggleActivity(item.contractId, activity.activityId, false)"/>
                            <div v-else-if="!isContractDisabled(item)" class="excluded-undo-icon">
                              <Icon
                              icon="mdi-undo"
                              iconColor="primary"
                              :small="false"
                              margin="mr-3"
                              :tooltipText="$t('undo')"
                              @icon-clicked="toggleActivity(item.contractId, activity.activityId, true)"/>
                            </div>
                          </div>
                          <div class="d-flex flex-column">
                            <span class="body-2 font-weight-medium">{{ activity.templateName }}</span>
                            <span class="caption" v-if="activity.counterpartyName">
                              {{ activity.counterpartyName }}
                              <Icon
                              v-if="activity.runtimeCounterparty !== null && activity.runtimeCounterparty !== undefined"
                              icon="mdi-alpha-r-circle-outline"
                              iconColor="info"
                              :tooltipText="`${$t('runtimeCounterparty')}: ${resolveRuntimeCounterparty(activity.runtimeCounterparty)}`"/>
                            </span>
                            <span class="caption" v-else-if="activity.runtimeCounterparty !== null && activity.runtimeCounterparty !== undefined">
                              {{ resolveRuntimeCounterparty(activity.runtimeCounterparty) }}
                            </span>
                            <span class="caption grey--text" v-if="activity.activityRateSheetDefinitionId">{{ getDefinitionName(activity.activityRateSheetDefinitionId) }}</span>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </template>
          </template>
        </v-window-item>

        <v-window-item :value="3">
          <v-row v-if="submitting" justify="center" class="py-8">
            <v-progress-circular indeterminate color="primary"/>
          </v-row>

          <template v-else>
            <DetailCard :title="$t('results')">
              <v-data-table
              :headers="resultHeaders"
              :items="submissionResults"
              dense
              hide-default-footer
              :items-per-page="-1"
              class="elevation-0">
                <template #item.status="{ item }">
                  <Icon v-if="item.success" :icon="'mdi-check-circle'" iconColor="success" :small="false"/>
                  <Icon v-else :icon="'mdi-alert-circle'" iconColor="error" :small="false"/>
                </template>
                <template #item.error="{ item }">
                  <span v-if="!item.success" class="error--text caption">{{ item.error }}</span>
                  <Icon
                  v-if="item.gap"
                  icon="mdi-information-outline"
                  :small="false"
                  margin="ml-1"
                  :tooltipText="$t('details')"
                  @icon-clicked="showUncoveredCombinations(item.gap)"/>
                </template>
              </v-data-table>
            </DetailCard>
          </template>
        </v-window-item>
      </v-window>
    </v-card-text>

    <v-card-actions class="py-4 px-4">
      <v-row no-gutters align="center">
        <v-col v-if="step !== 3" class="d-flex align-center" style="white-space: nowrap;">
          <Icon icon="mdi-information-outline" iconColor="grey" margin="mr-1"/>
          <em class="caption grey--text">{{ $t('scheduleRateChangeProductionOnly') }}</em>
        </v-col>
        <v-col cols="auto" v-if="step === 2">
          <v-btn text @click="goBack">
            {{ $t('back') }}
          </v-btn>
        </v-col>
        <v-spacer/>
        <v-col cols="auto">
          <v-btn
          v-if="step === 1"
          color="primary"
          :disabled="!canProceed || statsLoading"
          :loading="statsLoading"
          @click="doneClicked">
            {{ $t('done') }}
          </v-btn>
          <v-btn
          v-if="step === 2"
          color="primary"
          :disabled="totalSelectedActivities === 0 || submitting"
          :loading="submitting"
          @click="confirmClicked">
            {{ $t('confirmScheduledRateChange') }}
          </v-btn>
          <v-btn
          v-if="step === 3"
          color="primary"
          :disabled="submitting"
          @click="$emit('close')">
            {{ $t('done') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

    <v-dialog v-if="confirmApprovalDialog" v-model="confirmApprovalDialog" width="400px">
      <ConfirmDialog
      :title="$t('confirmSaveChanges')"
      :body="$t('confirmScheduledRateChangeApprovalMessage')"
      color="primary"
      :confirmText="$t('confirm')"
      @confirm="submitScheduledRateChange"
      @cancel="confirmApprovalDialog = false"/>
    </v-dialog>

    <Dialog :stateId="editEffectiveDateTimeDialogId" @close="onEditEffectiveDateTimeClosed">
      <DateTimeSelectionDialog
        v-if="editEffectiveDateTimeOpen"
        :title="$t('editEffectiveDate')"
        :buttonText="$t('done')"
        :startDate="editPromptDate"
        :startTime="editPromptTime"
        @confirm="confirmEditEffectiveDateTime"
        @cancel="cancelEditEffectiveDateTime"/>
    </Dialog>

    <Dialog :stateId="contractDetailDialogId" width="80vw">
      <ContractDetail
      v-if="focusedContract"
      :contractId="focusedContract.contractId"
      :hasPendingTickets="focusedContract.hasPendingTickets || false"/>
    </Dialog>

    <Dialog :stateId="uncoveredCombinationsDialogId" @close="uncoveredCombinations = null">
      <UncoveredLogCombinationsDialog
      v-if="uncoveredCombinations"
      :gap="uncoveredCombinations"
      fixOnDefinition/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import {
  ScheduledRateChangeCreateRequest,
  ScheduledActivityUpdateRequest,
  ScheduledDetailRateChangeRequest
} from '@/components/contract/scheduled-rate-change/ScheduledRateChange'
import { localToCompanyOffsetStartOfDay, utcToCompanyOffsetDstSensitive, companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import timezones from '@/components/settings-components/company-config/timezones.json'
import { RuntimeCounterparty, ContractApprovalStatus } from '@/utils/Enumerations'
import { expansionCoverageGapFromError } from '@/utils/ExpansionCoverage.js'
import IconHelpers from '@/utils/IconHelpers'
import { VersionedRateSheetStatus, assignVersionedRateSheetStatus } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'

export default {
  name: 'ScheduleDefinitionRateChangeDialog',

  components: {
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    ReadOnlyAttributeCard: () => import('@/components/helper/ReadOnlyAttributeCard.vue'),
    DateTimeSelectionDialog: () => import('@/components/helper/DateTimeSelectionDialog.vue'),
    UncoveredLogCombinationsDialog: () => import('@/components/activity/rate-sheet/UncoveredLogCombinationsDialog.vue')
  },

  props: {
    definitions: {
      type: Array,
      required: true
    },
    account: {
      type: Object,
      required: true
    },
    initialEffectiveDate: {
      type: String,
      default: null
    },
    initialEffectiveTime: {
      type: String,
      default: null
    }
  },

  data: () => ({
    IconHelpers,
    step: 1,
    effectiveDate: null,
    effectiveTime: null,
    showTimePicker: false,
    note: '',
    selectedDefinitionIds: [],
    resolvedDefinitions: [],
    impactedContracts: [],
    selectedActivities: {},
    contractSearch: '',
    statsLoading: false,
    confirmApprovalDialog: false,
    submitting: false,
    contractDetailDialogId: uniqueDialogId('schedule-contract-detail'),
    editEffectiveDateTimeDialogId: uniqueDialogId('edit-effective-date-time'),
    editEffectiveDateTimeOpen: false,
    focusedContract: null,
    submissionResults: [],
    editPromptDate: null,
    editPromptTime: null,
    uncoveredCombinations: null,
    uncoveredCombinationsDialogId: uniqueDialogId('definition-uncovered-log-combinations')
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),

    accountId () {
      return this.account.accountId
    },

    displayEffectiveDate () {
      if (!this.effectiveDate) return ''
      const tz = timezones.find(tz => Number(tz.offset) === this.companyInfo.timeZoneOffset)
      const tzAbbr = tz?.name?.split('(')[1]?.split(')')[0] ?? ''

      let utcDate = this.effectiveDate
      if (this.showTimePicker && this.effectiveTime) {
        utcDate = this.buildCompanyDateTime(this.effectiveDate, this.effectiveTime)
      }

      const adjusted = utcToCompanyOffsetDstSensitive(utcDate)
      const hasTime = adjusted.hours() !== 0 || adjusted.minutes() !== 0
      const formatted = adjusted.format(hasTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY')
      return hasTime && tzAbbr ? `${formatted} ${tzAbbr}` : formatted
    },

    canProceed () {
      return !!this.effectiveDate && this.selectedDefinitionIds.length > 0
    },

    contractHeaders () {
      return [
        { text: '', value: 'select', sortable: false, width: '40px' },
        { text: '', value: 'status', sortable: false, width: '64px' },
        { text: this.$t('account'), value: 'accountName' },
        { text: this.$t('destination'), value: 'destinationAccountName' },
        { text: this.$t('tract'), value: 'tractName' },
        { text: this.$t('setting'), value: 'setting' },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '80px', align: 'center' }
      ]
    },

    selectableContracts () {
      return this.impactedContracts.filter(c => !c.hasActiveApproval && !c.hasPendingScheduledRateChange)
    },

    totalSelectedActivities () {
      return Object.values(this.selectedActivities).reduce((sum, ids) => sum + ids.length, 0)
    },

    selectedContractCount () {
      return Object.values(this.selectedActivities).filter(ids => ids.length > 0).length
    },

    allActivitiesSelected () {
      return this.selectableContracts.length > 0 && this.selectableContracts.every(c => {
        const unlocked = this.unlockedActivities(c)
        return unlocked.length > 0 && (this.selectedActivities[c.contractId] || []).length === unlocked.length
      })
    },

    someActivitiesSelected () {
      return !this.allActivitiesSelected &&
        this.selectableContracts.some(c => (this.selectedActivities[c.contractId] || []).length > 0)
    },

    resultHeaders () {
      return [
        { text: '', value: 'status', sortable: false, width: '48px' },
        { text: this.$t('account'), value: 'contract.accountName' },
        { text: this.$t('destination'), value: 'contract.destinationAccountName' },
        { text: this.$t('tract'), value: 'contract.tractName' },
        { text: this.$t('setting'), value: 'contract.setting' },
        { text: this.$t('error'), value: 'error', sortable: false }
      ]
    }
  },

  created () {
    this.effectiveDate = this.initialEffectiveDate
      ? localToCompanyOffsetStartOfDay(moment(this.initialEffectiveDate).format())
      : localToCompanyOffsetStartOfDay(moment().add(1, 'days').format())
    if (this.initialEffectiveTime) {
      this.effectiveTime = this.initialEffectiveTime
      this.showTimePicker = true
    }
  },

  methods: {
    ...mapActions('rate-sheet-definition', ['fetchRateSheetDefinitionUsage', 'fetchVersionedRateSheetDefinition']),
    ...mapActions('scheduled-rate-change', ['createScheduledRateChange']),
    ...mapActions('contract-approvals', ['createContractApproval', 'updateContractApproval']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove', 'closeDialogById']),

    getDefinitionName (definitionId) {
      const match = this.resolvedDefinitions.find(
        ({ rateSheetDefinition }) => rateSheetDefinition.rateSheetDefinitionId === definitionId
      )
      return match?.rateSheetDefinition?.name ?? ''
    },

    openEditEffectiveDateTimeDialog () {
      this.editPromptDate = companyOffsetToLocalDate(this.effectiveDate, 'YYYY-MM-DD')
      this.editPromptTime = this.effectiveTime
      this.editEffectiveDateTimeOpen = true
      this.openOrUpdateDialog({ id: this.editEffectiveDateTimeDialogId, width: '380px' })
    },

    confirmEditEffectiveDateTime ({ date, time }) {
      this.effectiveDate = localToCompanyOffsetStartOfDay(moment(date).format())
      this.effectiveTime = time
      this.showTimePicker = true
      this.closeDialogById(this.editEffectiveDateTimeDialogId)
    },

    cancelEditEffectiveDateTime () {
      this.closeDialogById(this.editEffectiveDateTimeDialogId)
    },

    onEditEffectiveDateTimeClosed () {
      this.editEffectiveDateTimeOpen = false
    },

    toggleDefinition (definitionId) {
      const idx = this.selectedDefinitionIds.indexOf(definitionId)
      if (idx === -1) {
        this.selectedDefinitionIds.push(definitionId)
      } else {
        this.selectedDefinitionIds.splice(idx, 1)
      }
    },

    goBack () {
      this.step = 1
      this.$emit('resize', '400px')
    },

    showContractDetail (contract) {
      this.focusedContract = contract
      this.openOrUpdateDialog({ id: this.contractDetailDialogId, width: '80vw' })
    },

    showUncoveredCombinations (gap) {
      this.uncoveredCombinations = gap
      this.openOrUpdateDialog({ id: this.uncoveredCombinationsDialogId, width: '900px' })
    },

    buildCompanyDateTime (effectiveDate, timeString) {
      const [hours, minutes] = timeString.split(':').map(Number)
      const companyOffset = this.companyInfo.timeZoneOffset
      const datePortion = moment.utc(effectiveDate).add(companyOffset, 'hours').format('YYYY-MM-DD')
      let offset = companyOffset
      if (moment(datePortion).isDST()) {
        offset = companyOffset + 1
      }
      return moment.utc(`${datePortion}T00:00:00`)
        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
        .add(offset * -1, 'hours')
        .utc()
        .format()
    },

    async doneClicked () {
      if (this.selectedDefinitionIds.length === 0) {
        this.setSnackError(this.$t('noDefinitionsSelected'))
        return
      }

      this.statsLoading = true
      try {
        const selectedDefs = this.definitions.filter(d =>
          this.selectedDefinitionIds.includes(d.rateSheetDefinitionId)
        )

        const versionResults = await Promise.all(
          selectedDefs.map(def =>
            this.fetchVersionedRateSheetDefinition({
              accountId: this.accountId,
              rateSheetDefinitionId: def.rateSheetDefinitionId
            })
          )
        )

        this.resolvedDefinitions = selectedDefs
          .map((def, i) => {
            const versions = versionResults[i] || []
            assignVersionedRateSheetStatus(versions)
            const activeVersion = versions.find(v => v.status?.id === VersionedRateSheetStatus.Active.id)
            return activeVersion ? { rateSheetDefinition: def, activeVersion } : null
          })
          .filter(Boolean)

        if (this.resolvedDefinitions.length === 0) {
          this.setSnackError(this.$t('noActiveVersion'))
          return
        }

        const statsContracts = await this.fetchRateSheetDefinitionUsage({
          accountId: this.accountId,
          rateSheetDefinitionIds: this.selectedDefinitionIds
        })

        if (!statsContracts || statsContracts.length === 0) {
          this.setSnackError(this.$t('noImpactedContracts'))
          return
        }

        this.impactedContracts = statsContracts.map(contract => ({
          ...contract,
          activities: contract.activities.filter(a =>
            this.selectedDefinitionIds.includes(a.activityRateSheetDefinitionId)
          )
        })).filter(c => c.activities.length > 0)

        if (this.impactedContracts.length === 0) {
          this.setSnackError(this.$t('noImpactedContracts'))
          return
        }

        const newSelected = {}
        this.selectableContracts.forEach(c => {
          this.$set(newSelected, c.contractId, this.unlockedActivities(c).map(a => a.activityId))
        })
        this.selectedActivities = newSelected
        this.step = 2
        this.$emit('resize', '85vw')
      } catch (e) {
        console.error('Failed to fetch rate sheet definition usage', e)
      } finally {
        this.statsLoading = false
      }
    },

    confirmClicked () {
      if (this.totalSelectedActivities === 0) {
        this.setSnackError(this.$t('selectAtLeastOneContract'))
        return
      }
      if (this.companyInfo.requireApprovalForContractModifications) {
        this.confirmApprovalDialog = true
      } else {
        this.submitScheduledRateChange()
      }
    },

    async submitScheduledRateChange () {
      this.confirmApprovalDialog = false
      this.submitting = true
      this.submissionResults = []
      this.step = 3

      let effectiveDateTime
      if (this.showTimePicker && this.effectiveTime) {
        effectiveDateTime = this.buildCompanyDateTime(this.effectiveDate, this.effectiveTime)
      } else {
        effectiveDateTime = this.buildCompanyDateTime(this.effectiveDate, '00:00')
      }

      const contractsToSubmit = this.impactedContracts.filter(c =>
        (this.selectedActivities[c.contractId] || []).length > 0
      )

      const requiresApproval = this.companyInfo.requireApprovalForContractModifications

      for (const contract of contractsToSubmit) {
        try {
          let contractApprovalId = null
          if (requiresApproval) {
            const approvalResponse = await this.createContractApproval({ contractId: contract.contractId })
            if (!approvalResponse) {
              this.submissionResults.push({ contract, success: false, error: this.$t('approvalCreationFailed') })
              continue
            }
            contractApprovalId = approvalResponse.contractApprovalId
          }

          const selectedActivityIds = this.selectedActivities[contract.contractId] || []
          const selectedActivities = contract.activities.filter(a => selectedActivityIds.includes(a.activityId))

          const updatedActivities = selectedActivities
            .map((activity) => {
              const rateSheet = this.buildRateSheetFromDefinition(activity)
              if (!rateSheet) return null

              return new ScheduledActivityUpdateRequest(
                effectiveDateTime,
                contract.contractId,
                activity.activityTemplateId,
                activity.runtimeCounterparty === null ? (activity.contractorId ?? null) : null,
                activity.runtimeCounterparty,
                activity.recoveryMode,
                activity.advanceId,
                activity.baseCostPayBy,
                activity.baseCostPayOn,
                activity.baseCostRate,
                rateSheet,
                activity.activityDetails?.map(d =>
                  new ScheduledDetailRateChangeRequest(d.product.productId, d.cost?.rate)
                ) ?? [],
                activity.activityId,
                activity.deductFromActivityIdentifier,
                activity.deductFromActivityId
              )
            })
            .filter(Boolean)

          const request = new ScheduledRateChangeCreateRequest(
            contract.contractId,
            effectiveDateTime,
            [],
            updatedActivities,
            [],
            this.note
          )

          await this.createScheduledRateChange(request)

          if (requiresApproval && contractApprovalId) {
            await this.updateContractApproval({
              contractApprovalId,
              contractId: contract.contractId,
              status: ContractApprovalStatus.PendingReview.value
            })
          }

          this.submissionResults.push({ contract, success: true, error: null })
        } catch (e) {
          const error = e?.response?.data?.message ?? e?.message ?? this.$t('unknownError')
          this.submissionResults.push({ contract, success: false, error, gap: expansionCoverageGapFromError(e) })
        }
      }

      this.submitting = false
      this.$emit('submitted')
    },

    buildRateSheetFromDefinition (activity) {
      const defId = activity.activityRateSheetDefinitionId
      if (!defId) return null

      const match = this.resolvedDefinitions.find(
        ({ rateSheetDefinition }) => rateSheetDefinition.rateSheetDefinitionId === defId
      )
      if (!match) return null

      const { activeVersion } = match
      const clonedLines = cloneDeep(activeVersion.rateSheetDefinitionLines).map(defLine => ({
        rate: defLine.rate,
        speciesGroupId: defLine.speciesGroup?.speciesGroupId ?? defLine.speciesGroupId ?? null,
        lengthGroupId: defLine.lengthGroup?.lengthGroupId ?? defLine.lengthGroupId ?? null,
        diameterGroupId: defLine.diameterGroup?.diameterGroupId ?? defLine.diameterGroupId ?? null,
        gradeId: defLine.grade?.gradeId ?? defLine.gradeId ?? null,
        endUseSortId: defLine.endUseSort?.endUseSortId ?? defLine.endUseSortId ?? null
      }))

      return {
        rateSheetId: activity.rateSheetId,
        rateSheetDefinitionId: defId,
        rateSheetLines: clonedLines
      }
    },

    allActivitiesSelectedForContract (contract) {
      const selected = this.selectedActivities[contract.contractId] || []
      const unlocked = this.unlockedActivities(contract)
      return !this.isContractDisabled(contract) && unlocked.length > 0 && selected.length === unlocked.length
    },

    someActivitiesSelectedForContract (contract) {
      const selected = this.selectedActivities[contract.contractId] || []
      const unlocked = this.unlockedActivities(contract)
      return !this.isContractDisabled(contract) && selected.length > 0 && selected.length < unlocked.length
    },

    isActivitySelected (contractId, activityId) {
      return (this.selectedActivities[contractId] || []).includes(activityId)
    },

    toggleActivity (contractId, activityId, value) {
      const current = [...(this.selectedActivities[contractId] || [])]
      if (value) {
        if (!current.includes(activityId)) current.push(activityId)
      } else {
        const idx = current.indexOf(activityId)
        if (idx !== -1) current.splice(idx, 1)
      }
      this.$set(this.selectedActivities, contractId, current)
    },

    toggleContractActivities (contract, value) {
      if (this.isContractDisabled(contract)) return
      const unlocked = this.unlockedActivities(contract)
      this.$set(
        this.selectedActivities,
        contract.contractId,
        value ? unlocked.map(a => a.activityId) : []
      )
    },

    toggleAllActivities (value) {
      const newSelected = {}
      if (value) {
        this.selectableContracts.forEach(c => {
          this.$set(newSelected, c.contractId, this.unlockedActivities(c).map(a => a.activityId))
        })
      }
      this.selectedActivities = newSelected
    },

    unlockedActivities (contract) {
      return contract.activities.filter(a => !a.hasLockedLine)
    },

    isContractDisabled (item) {
      return item.hasActiveApproval || item.hasPendingScheduledRateChange
    },

    contractRowClass (item) {
      return this.isContractDisabled(item) ? 'disabled-contract-row' : ''
    },

    resolveRuntimeCounterparty (val) {
      return RuntimeCounterparty.fromInt(val)
    }
  }
}
</script>

<style>
.schedule-contracts-table .disabled-contract-row {
  background-color: #f5f5f5;
}

.schedule-contracts-table .disabled-contract-row td {
  color: rgba(0, 0, 0, 0.35) !important;
}

.schedule-contracts-table .disabled-contract-row:hover {
  background-color: #f5f5f5 !important;
}

.expanded-activity-cell {
  padding: 6px 16px 6px 48px !important;
  box-shadow: none !important;
  background-color: #fafafa;
}

.expanded-activity-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.expanded-activity-card {
  border-color: #e0e0e0 !important;
}

.excluded-activity {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}

.excluded-activity > div > span {
  opacity: 0.4;
}

.excluded-activity .excluded-undo-icon {
  opacity: 1;
}

.disabled-activity {
  opacity: 0.4;
}
</style>
